import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { commonRoutes } from 'constants/web-app-constants';
import { ChildOnboardingStore } from '../../+state/child-onboarding.store';
import { ChildOnboarding } from '../../entities/child-onboarding/child-onboarding.model';

export type OnboardingStep =
  | 'redirect'
  | 'person'
  | 'residence'
  | 'mobileNumber'
  | 'nationality'
  | 'bankAccount'
  | 'fileUpload'
  | 'legalGuardian2'
  | 'email'
  | 'emailConfirm'
  | 'emailConfirmed'
  | 'conclusionOfContract';

// Step definition with all properties in one place
interface OnboardingStepConfig {
  id: OnboardingStep;
  order: number;
  route: string;
}

// Define steps in a single source of truth
const ONBOARDING_STEPS: OnboardingStepConfig[] = [
  { id: 'redirect', order: 0, route: commonRoutes.postlogin.child_onboarding.redirect },
  { id: 'person', order: 1, route: commonRoutes.postlogin.child_onboarding.person },
  { id: 'residence', order: 2, route: commonRoutes.postlogin.child_onboarding.residence },
  { id: 'mobileNumber', order: 3, route: commonRoutes.postlogin.child_onboarding.mobile_number },
  { id: 'nationality', order: 4, route: commonRoutes.postlogin.child_onboarding.nationality },
  { id: 'bankAccount', order: 5, route: commonRoutes.postlogin.child_onboarding.bank_account },
  { id: 'fileUpload', order: 6, route: commonRoutes.postlogin.child_onboarding.identification },
  { id: 'legalGuardian2', order: 7, route: commonRoutes.postlogin.child_onboarding.legal_guardian_2 },
  { id: 'email', order: 8, route: commonRoutes.postlogin.child_onboarding.sign_up },
  { id: 'emailConfirm', order: 9, route: commonRoutes.postlogin.child_onboarding.email_confirm },
  { id: 'conclusionOfContract', order: 10, route: commonRoutes.postlogin.child_onboarding.conclusion_of_contract },
];

// Create lookup maps (computed once at initialization)
const stepToOrderMap: Record<OnboardingStep, number> = {} as Record<OnboardingStep, number>;
const orderToStepMap: Record<number, OnboardingStep> = {};
const stepToRouteMap: Record<OnboardingStep, string> = {} as Record<OnboardingStep, string>;
const routeToStepMap: Record<string, OnboardingStep> = {};

ONBOARDING_STEPS.forEach((step) => {
  stepToOrderMap[step.id] = step.order;
  orderToStepMap[step.order] = step.id;
  stepToRouteMap[step.id] = step.route;
  routeToStepMap[step.route] = step.id;
});

@Injectable()
export class ChildOnboardingStepService {
  readonly #store = inject(ChildOnboardingStore);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #customerStateFacade = inject(CustomerStateFacade);

  #currentStepId: OnboardingStep | null = null;

  public initialize(): void {
    this.#customerStateFacade.loadCustomerBaseData();
    this.#initStepByCurrentRoute();
  }

  public routeToHomeOnInvalidData() {
    if (!this.#store.selectedChildOnboarding() && !this.#store.selectedSituation()) {
      this.redirectToHome();
    }
  }

  public initStepResetSuccess(): void {
    this.#initStepByCurrentRoute();
    if (this.#currentStepId === null) {
      this.redirectToHome();
      return;
    }
    this.#store.resetSuccessStates();
    this.redirectToNextStep();
  }

  public redirectToNextStep(force = false): void {
    const nextStepId = this.getNextStep(this.#store.selectedChildOnboarding());
    if (!nextStepId) {
      this.redirectToHome();
      return;
    }

    if (this.#currentStepId) {
      const currentOrder = stepToOrderMap[this.#currentStepId];
      const nextOrder = stepToOrderMap[nextStepId];

      // Prevent moving foward without completeing the current step
      if (!force && currentOrder < nextOrder) {
        return;
      }
    }

    this.#navigateToStep(nextStepId);
  }

  public goToNextStep(): void {
    let nextStepId: OnboardingStep | null = null;

    if (this.#currentStepId) {
      const currentOrder = stepToOrderMap[this.#currentStepId];
      const nextOrder = currentOrder + 1;
      nextStepId = orderToStepMap[nextOrder];
    }
    this.#navigateToStep(nextStepId);
  }

  public goToPreviousStep(): void {
    let prevStepId: OnboardingStep | null = null;

    if (this.#currentStepId) {
      const currentOrder = stepToOrderMap[this.#currentStepId];
      const prevOrder = currentOrder - 1;
      prevStepId = orderToStepMap[prevOrder];
    }
    this.#navigateToStep(prevStepId);
  }

  #navigateToStep(stepId: OnboardingStep | null): void {
    if (!stepId) {
      this.redirectToHome();
      return;
    }

    this.#currentStepId = stepId;
    const route = stepToRouteMap[stepId];
    const id = this.#store.selectedId();

    const isFromRedirect = this.#router.url.includes(stepToRouteMap['redirect']);

    this.#router.navigate([route], {
      queryParams: { id },
      relativeTo: this.#route,
      replaceUrl: isFromRedirect,
    });
  }

  public redirectToHome() {
    this.#router.navigate([commonRoutes.postlogin.child_depot], { relativeTo: this.#route });
  }

  #initStepByCurrentRoute(): void {
    const currentPath = this.#router.url?.split('?')[0];
    this.#currentStepId = routeToStepMap[currentPath] || null;
  }

  public getNextStep(data: ChildOnboarding): OnboardingStep | null {
    if (!data) {
      return null;
    }

    const { vorname, nachname, geburtsdatum, geburtsort, geburtsland } = data;
    if (!vorname || !nachname || !geburtsdatum || !geburtsort || !geburtsland) {
      return 'person';
    }
    const { strasse, hausnummer, plz, ort, land } = data;
    if (!strasse || !hausnummer || !plz || !ort || !land) {
      return 'residence';
    }
    if (!data.mobileNumber) {
      return 'mobileNumber';
    }
    const { nationality, steuerland, steuerid, cEmail } = data;
    if (!nationality || !steuerland || (!steuerid && !cEmail)) {
      //tin is required now, to support the new tax information flow email is checked as well
      return 'nationality';
    }
    const { iban, kontoinhaber } = data;
    if (!iban || !kontoinhaber) {
      return 'bankAccount';
    }
    if (!data.uploadFileReferences?.length) {
      return 'fileUpload';
    }
    if (!data.legalGuardian2Id) {
      return 'legalGuardian2';
    }
    if (!data.cEmail) {
      return 'email';
    }
    if (!data.cEmailVerified && data.processStatus === 'REGISTER') {
      return 'emailConfirm';
    }
    if (data.cEmailVerified && data.processStatus !== 'AWAITING_LG2CONFIRMATION' && data.processStatus !== 'CONFIRMED') {
      return 'conclusionOfContract';
    }
    return null;
  }
}
